import Vue from "vue";
import Router from "vue-router";
import { api, isNetworkError } from "@/state/services";
import { canAccessRegionalMenu, firstRegionalUserPath } from "@/utils/regional-user-permissions";
import native from "@/native";
import { isEmbeddedWebView } from "@/utils/webview";
import routes from "./routes-list";

Vue.use(Router);

// Registra a abertura do app (biTeams > Acessos ao App) uma única vez por
// carregamento e por token — o GET user abaixo roda a cada navegação e não
// serve de contador. Guardar o token (e não um booleano) faz um login novo
// sem recarregar a página (logout → login de outra pessoa) contar de novo.
// Fire-and-forget: falha aqui nunca pode atrapalhar a navegação.
let acessoRegistradoToken = null;

function registrarAcesso() {
  const token = localStorage.getItem("token");
  if (!token || acessoRegistradoToken === token) {
    return;
  }
  acessoRegistradoToken = token;

  api
    .post("acesso", { origem: isEmbeddedWebView() ? "app" : "web" })
    .catch(() => {});
}

// Registra cada tela vista (biTeams > Acessos ao App > Páginas Mais Acessadas).
// Vai o MOLDE da rota ("/contracts/:id"), nunca a URL real: telas iguais somam
// numa linha só e nenhum id/token sai daqui. Mudar só a query string da mesma
// tela não conta de novo. Fire-and-forget, como o registrarAcesso.
let ultimaPaginaRegistrada = null;

function registrarPagina(to) {
  const rota = to.matched[to.matched.length - 1];
  if (!rota || !to.meta.authRequired || !localStorage.getItem("token")) {
    return;
  }

  const chave = localStorage.getItem("token") + "|" + rota.path;
  if (ultimaPaginaRegistrada === chave) {
    return;
  }
  ultimaPaginaRegistrada = chave;

  api
    .post("acesso/pagina", {
      pagina: rota.path || "/",
      titulo: (to.meta.menu && to.meta.menu.label) || "",
      origem: isEmbeddedWebView() ? "app" : "web",
    })
    .catch(() => {});
}

const router = new Router({
  mode: "history",
  base: "/",
  routes,
});

router.beforeEach((to, from, next) => {
  const loggedIn = localStorage.getItem("token");

  document.title = process.env.VUE_APP_TITLE;

  if (to.meta.authRequired && !loggedIn) {
    return next("/login");
  }

  if (!to.meta.authRequired) {
    return next();
  }

  if (to.meta.authRequired && loggedIn) {
    api
      .get("user")
      .then((response) => {
        if (response.data && response.data.status == 'success') {
          router.app.$store.state.account.user = response.data.user;
          registrarAcesso();

          const isAfiliadosSectionRoute = to.path.indexOf('/afiliados/') === 0;
          const regulamentoStatus = response.data.user.afiliados_regulamento_status;

          if (isAfiliadosSectionRoute && regulamentoStatus !== 'accepted') {
            // Cobre tanto 'pending' quanto 'declined': recusar não bloqueia
            // permanentemente, só volta pra home — a cada nova tentativa de
            // entrar em Afiliados o regulamento é reapresentado.
            //
            // Não limpamos afiliadosRegulamentoPendingPath em nenhum outro
            // ponto deste guard: o próprio next('/') abaixo dispara uma nova
            // passagem por este beforeEach (para o path '/'), e limpar a
            // flag ali apagaria o valor antes do modal (montado no layout
            // main.vue) conseguir reagir a ele. A flag só é limpa pelo
            // próprio modal, ao aceitar ou recusar — o que é seguro, pois
            // enquanto ele estiver aberto (backdrop não fechável) o usuário
            // não consegue navegar para nenhum outro lugar mesmo.
            router.app.$store.state.account.afiliadosRegulamentoPendingPath = to.path;
            return next('/');
          }

          if (!canAccessRegionalMenu(response.data.user, to.path)) {
            return next(firstRegionalUserPath(response.data.user));
          }

          if (
            response.data.user.atualizacad === "pending" &&
            to.path !== "/atualizacad"
          ) {
            return next("/atualizacad");
          } else if (response.data.user.documents &&
              response.data.user.documents == 'pending' &&
              to.path !== "/documents") {
            return next("/documents");
          } else if (
            response.data.user.contracts &&
            response.data.user.contracts.length > 0 &&
            to.path != "/contracts/:id"
          ) {
            response.data.user.contracts.forEach(con => {
              return next("/contracts/" + con.id);
            })
          } else if (
            response.data.order &&
            response.data.order.id &&
            to.path != "/store/orders"
          ) {
            return next("/store/order/" + response.data.order.id);
          }

          return next();
        } else {
          return next("/login");
        }
      })
      .catch(error => {
        // SEM REDE NÃO É SESSÃO INVÁLIDA.
        //
        // Este `catch` mandava para "/login" em qualquer erro — inclusive
        // quando a chamada simplesmente não saiu por falta de sinal. E a tela
        // de login chama `logout()` no `created()`, apagando `token` e `user`.
        // Ou seja: abrir o aplicativo no elevador deslogava o consultor de
        // verdade, e ele tinha de digitar CPF e senha de novo.
        //
        // Agora, sem resposta HTTP, mantemos a sessão e deixamos a navegação
        // seguir. A tela de "sem conexão" do container (ou o estado de erro da
        // própria tela, no navegador) explica o que está acontecendo, e a
        // próxima navegação com rede revalida o token normalmente.
        if (isNetworkError(error)) {
          return next();
        }

        if (error) {
          return next("/login");
        }
      });
  }
});

// afterEach só roda em navegação confirmada: redirecionamentos do guard acima
// (login, atualizacad, contrato pendente…) não contam a tela que foi barrada.
router.afterEach((to) => {
  registrarPagina(to);
});

// Deep link da notificação: o container entrega o caminho ("/tickets/12") e o
// router leva o usuário até lá. Registrado uma vez, aqui, porque é o único
// lugar que conhece a instância do router. Fora do aplicativo isso é no-op.
native.setNavigator(function (path) {
  if (router.currentRoute && router.currentRoute.fullPath === path) {
    return;
  }

  router.push(path).catch(function () {
    /* navegação duplicada ou rota inexistente: não é motivo para quebrar nada */
  });
});

export default router;
